<template>
  <div class="schedule-add-date-time-wrapper">
    <div class="schedule-blurbs-button">
      <div class="schedule-blurbs">
        If dayparts are not specified, the {{ itemType }} is always active.
      </div>
      <div style="text-align: right">
        <v-btn variant="flat" color="accent" @click="addDateTimeDialog = true">
          <v-icon start>md:add</v-icon>
          <span>Add</span>
        </v-btn>
      </div>
    </div>
    <v-table class="rf-settings-table-data">
      <template #default>
        <thead>
          <tr>
            <th class="text-left">Day of the week</th>
            <th class="text-left">Start Time</th>
            <th class="text-left">End time</th>
            <th></th>
          </tr>
        </thead>
        <tbody v-if="schedule.length > 0">
          <tr v-for="(item, index) in schedule" :key="index">
            <td>{{ weekDays[item[0]] }}</td>
            <td>{{ StringUtils.hhmmDisplay(item[1]) }}</td>
            <td>{{ StringUtils.hhmmDisplay(item[2]) }}</td>
            <td align="right">
              <v-btn variant="flat" size="small" @click="deleteTimeRange(index)">
                <v-icon size="18">md:clear</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td align="center" colspan="4">No date and time has been set up.</td>
          </tr>
        </tbody>
      </template>
    </v-table>
    <v-dialog v-model="addDateTimeDialog" maxWidth="550">
      <v-card flat>
        <v-card-title>Add new schedule</v-card-title>
        <v-card-text>
          <div class="flex flex-col gap-4">
            <RfSelect
              id="weekDay"
              v-model="weekDay"
              variant="outlined"
              :items="weekDays"
              title="Day of the week"
              name="weekDay"
              sameWidth
            ></RfSelect>
            <div class="flex items-center gap-4">
              <RfTimePicker ref="startTime" class="flex-1" label="Start Time" autoWidth sameWidth />
              <RfTimePicker ref="endTime" class="flex-1" label="End Time" autoWidth sameWidth />
            </div>
            <RfAlert v-if="errorTimeRangeComputed" type="error">
              Start time must be less than end time
            </RfAlert>
          </div>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            size="large"
            variant="outlined"
            class="cancel-btn"
            width="100px"
            @click="addDateTimeDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            size="large"
            variant="flat"
            color="success"
            width="160px"
            @click="appendDayTimeRange"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import StringUtils from "@/utils/StringUtils";
import RfTimePicker from "@/components/RfTimePicker.vue";
import { useTemplateRef, ref, computed, watch } from "vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";

const props = defineProps(["scheduleRanges", "itemType"]);

const addDateTimeDialog = ref(false);
const startTime = useTemplateRef("startTime");
const endTime = useTemplateRef("endTime");

const weekDay = ref("Sunday");
const schedule = ref([...props.scheduleRanges]);

watch(
  () => props.scheduleRanges,
  to => {
    if (to?.length) schedule.value = [...to];
  },
);

const weekDays = computed(() => {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
});

const errorTimeRangeComputed = computed(() => {
  const startTimeValue = startTime.value?.time;
  const endTimeValue = endTime.value?.time;
  return startTimeValue >= endTimeValue && !(startTimeValue == 47 && endTimeValue == 0);
});

const appendDayTimeRange = () => {
  if (errorTimeRangeComputed.value) return;

  const startTimeValue = startTime.value?.time;
  const endTimeValue = endTime.value?.time;
  schedule.value = [
    ...schedule.value,
    [
      weekDays.value.indexOf(weekDay.value),
      StringUtils.hhmmBy30MinIntervalIndex2(startTimeValue),
      StringUtils.hhmmBy30MinIntervalIndex2(endTimeValue),
    ],
  ];
  startTime.value.time = 0;
  endTime.value.time = 0;
  addDateTimeDialog.value = false;
};

const deleteTimeRange = index => {
  schedule.value = schedule.value.filter((_, pos) => pos != index);
};

defineExpose({ schedule });
</script>
